import {getFallbackGameImage} from "@assets/images/fallbackGameImage";
import {IconHeart, IconHeartFilled, IconPlayerPlay} from "@tabler/icons-react";
import PropTypes from "prop-types";
import {memo} from "react";
import {useThemeStore} from "@/store/useTheme.store";
import styles from "./CarouselGameCard.module.scss";

const CarouselGameCard = ({
  name,
  icon,
  providerLabel,
  onClick,
  rank,
  badge,
  isFav = false,
  onFavorite,
  favoriteAddLabel = "Add to favorites",
  favoriteRemoveLabel = "Remove from favorites",
  loading = false,
  className = "",
}) => {
  const theme = useThemeStore(state => state.theme);
  const fallbackImage = getFallbackGameImage(theme);

  const handleKeyDown = e => {
    if ((e.key === "Enter" || e.key === " ") && onClick) {
      e.preventDefault();
      onClick();
    }
  };

  if (loading) {
    return (
      <div className={`${styles.card} ${className}`.trim()}>
        <div className={styles.loadingIcon} />
      </div>
    );
  }

  return (
    <div
      className={`${styles.card} ${className}`.trim()}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-label={name}
    >
      <img
        src={icon || fallbackImage}
        alt={name}
        onError={e => {
          e.target.onerror = null;
          e.target.src = getFallbackGameImage(useThemeStore.getState().theme);
        }}
        className={styles.cardImg}
        loading="lazy"
      />
      <div className={styles.gradientOverlay} />
      {rank != null && <span className={styles.carNum}>{rank}</span>}
      {badge === "hot" && <span className={styles.hotBadge}>HOT</span>}
      <div className={styles.playOverlay}>
        <div className={styles.playBtn}>
          <IconPlayerPlay
            size={16}
            fill="currentColor"
          />
        </div>
      </div>
      {onFavorite && (
        <button
          type="button"
          className={`${styles.favBtn} ${isFav ? styles.favActive : ""}`}
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            onFavorite(e);
          }}
          aria-label={isFav ? favoriteRemoveLabel : favoriteAddLabel}
        >
          {isFav ? (
            <IconHeartFilled
              size={12}
              color="currentColor"
            />
          ) : (
            <IconHeart
              size={12}
              stroke={2}
              color="currentColor"
            />
          )}
        </button>
      )}
      <div className={styles.cardFooter}>
        <span className={styles.gameName}>{name}</span>
        {providerLabel ? <span className={styles.gameProvider}>{providerLabel.toUpperCase()}</span> : null}
      </div>
    </div>
  );
};

CarouselGameCard.propTypes = {
  name: PropTypes.string,
  icon: PropTypes.string,
  providerLabel: PropTypes.string,
  onClick: PropTypes.func,
  rank: PropTypes.number,
  badge: PropTypes.oneOf(["hot"]),
  isFav: PropTypes.bool,
  onFavorite: PropTypes.func,
  favoriteAddLabel: PropTypes.string,
  favoriteRemoveLabel: PropTypes.string,
  loading: PropTypes.bool,
  className: PropTypes.string,
};

export default memo(CarouselGameCard);
export {normalizeCarouselGame} from "./normalizeCarouselGame";
